import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { tenantBannerState } from '../lib/tenant-deletion-banner'
import './TenantDeletionBanner.scss'

/**
 * Global, non-dismissable banner shown when the caller's active organization is
 * pending deletion (`pending_deletion`) or has been deactivated (`deleted`).
 *
 * Self-gating: reads `profile.activeTenant` from `GET /users/me` and renders
 * nothing for active tenants, so it can be mounted unconditionally in Layout
 * alongside the other global banners.
 */
const TenantDeletionBanner = () => {
  const { t, i18n } = useTranslation('common')
  const { profile } = useAuth()

  const state = tenantBannerState(profile?.activeTenant)
  if (!state) return null

  if (state.variant === 'deactivated') {
    return (
      <div className="tenant-deletion-banner tenant-deletion-banner--deactivated" role="alert">
        <span className="tenant-deletion-banner__icon" aria-hidden="true">&#x26A0;&#xFE0F;</span>
        <span className="tenant-deletion-banner__text">
          {t('tenantDeletion.bannerDeactivated')}
        </span>
      </div>
    )
  }

  // pending variant
  const formattedDate = state.purgeDate
    ? new Intl.DateTimeFormat(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(state.purgeDate)
    : null

  return (
    <div className="tenant-deletion-banner tenant-deletion-banner--pending" role="alert">
      <span className="tenant-deletion-banner__icon" aria-hidden="true">&#x26A0;&#xFE0F;</span>
      <span className="tenant-deletion-banner__text">
        {formattedDate
          ? t('tenantDeletion.bannerPending', { date: formattedDate })
          : t('tenantDeletion.bannerPendingNoDate')}
      </span>
    </div>
  )
}

export default TenantDeletionBanner
